import { useMemo } from 'react'

import { useSelector } from 'react-redux'
import Cookie from 'js-cookie'
import { Tag } from 'antd'
import { faCheck, faEye, faXmark } from '@fortawesome/pro-solid-svg-icons'

import { selectTranslate } from '../../../../reducers/LocaleReducer/LocalReducer'
import DataTable from '../../../../Components/antd/Tables/DataTable/DataTable'
import SchoolRequestFilters, { getActiveFiltersCount } from './SchoolRequestFilters'
import { Action } from '../../../../Components/antd/Tables/DataTable/utils/actions'
import { buildFilters, Filter } from '../../../../Components/antd/Tables/DataTable/utils/tableFilter'
import { OrderBy } from '../../../../utils/constants'
import {
  countLines, countPlaces, countTwinRequests, getCoordinatorFullname, getCreatedAt, getMergeTag, getSchoolName,
  getSectionsLabel, getSectorsLabel, getStateTag, isClosed, isExpired, joinsExistingSchool
} from '../../../../utils/entities/schoolPortalRequest'

const SCHOOL_CELL_STYLE = { gap: '6px' }

const getMergeTooltip = (r, t) => {
  if (joinsExistingSchool(r)) {
    return t('school_portal_request.merge.joins_existing_tooltip', { school: getSchoolName(r) })
  }

  const twins = countTwinRequests(r)

  return twins > 0 ? t('school_portal_request.merge.has_twins_tooltip', { count: twins }) : null
}

const SchoolRequestsTable = ({
  data, loading, metadata, parameters, onAccept, onConsult, onFiltersChange, onParametersChange, onRefuse, onResetFilters
}) => {
  const t = useSelector(selectTranslate)

  const maxHeight = useMemo(() => `calc(100vh - ${Cookie.get('switch_user_id') ? '235px' : '194px'})`, [Cookie.get('switch_user_id')])

  const filters = useMemo(() => buildFilters([
    new Filter({
      key: 'filters',
      label: t('common.filters'),
      component: SchoolRequestFilters,
      props: { filters: parameters.filters, onFiltersChange }
    })
  ]), [parameters.filters, onFiltersChange, t])

  const columns = useMemo(() => [
    {
      title: t('data_table.column_title.state'),
      orderBy: OrderBy.STATE,
      key: r => {
        const tag = getStateTag(r.state)

        return (
          <div className='flex-row ai-center' style={SCHOOL_CELL_STYLE}>
            <Tag color={tag.color}>{t(tag.label)}</Tag>
            {isExpired(r) && <Tag color='orange'>{t('school_portal_request.expired')}</Tag>}
          </div>
        )
      },
      tooltip: r => isExpired(r) ? t('school_portal_request.expired_tooltip') : null
    },
    {
      title: t('data_table.column_title.creation_date'),
      orderBy: OrderBy.CREATED_AT,
      key: getCreatedAt
    },
    {
      title: t('data_table.column_title.school'),
      orderBy: OrderBy.SCHOOL,
      key: r => {
        const mergeTag = getMergeTag(r)

        if (mergeTag === null) {
          return getSchoolName(r)
        }

        return (
          <div className='flex-row ai-center' style={SCHOOL_CELL_STYLE}>
            <span>{getSchoolName(r)}</span>
            <Tag color={mergeTag.color}>{t(mergeTag.label)}</Tag>
          </div>
        )
      },
      tooltip: r => getMergeTooltip(r, t)
    },
    {
      title: t('school_requests_table.column_title.coordinator'),
      key: r => getCoordinatorFullname(r.coordinator),
      tooltip: r => r.coordinator?.email
    },
    {
      title: t('school_requests_table.column_title.volume'),
      key: r => t('school_requests_table.volume', { lines: countLines(r), places: countPlaces(r) })
    },
    {
      title: t('data_table.column_title.section'),
      key: getSectionsLabel,
      tooltip: getSectionsLabel
    },
    {
      title: t('data_table.column_title.sector'),
      key: getSectorsLabel,
      tooltip: getSectorsLabel
    }
  ], [t])

  const actions = useMemo(() => [
    new Action({
      title: 'school_requests_table.actions.consult',
      icon: faEye,
      buttonType: 'default',
      onClick: onConsult
    }),
    new Action({
      title: r => isExpired(r)
        ? 'school_requests_table.actions.accept_expired'
        : 'school_requests_table.actions.accept',
      icon: faCheck,
      disabled: r => isClosed(r) || isExpired(r),
      onClick: onAccept
    }),
    new Action({
      title: 'school_requests_table.actions.refuse',
      icon: faXmark,
      buttonType: 'danger',
      disabled: isClosed,
      onClick: onRefuse
    })
  ], [onAccept, onConsult, onRefuse])

  return (
    <div style={{ height: maxHeight }}>
      <DataTable
        actions={actions}
        columns={columns}
        data={data}
        filters={filters}
        loading={loading}
        metadata={metadata}
        parameters={parameters}
        getActiveFiltersCount={getActiveFiltersCount}
        onParametersChange={onParametersChange}
        onResetFilters={onResetFilters}
      />
    </div>
  )
}

export default SchoolRequestsTable
